import React from 'react';
import { Users, Mail, RefreshCw } from 'lucide-react';

interface CreditBarProps {
  balance: number | null;
  balanceLoading?: boolean;
  balanceError?: boolean;
  onRetryBalance?: () => void;
  overBudget?: boolean;
  icon: 'people' | 'mail';
  unitLabel: string;
  children?: React.ReactNode;
}

const CreditBar: React.FC<CreditBarProps> = ({
  balance,
  balanceLoading = false,
  balanceError = false,
  onRetryBalance,
  overBudget = false,
  icon,
  unitLabel,
  children,
}) => {
  const Icon = icon === 'mail' ? Mail : Users;
  const isOverBudget = overBudget && !balanceError;

  const containerClass = balanceError
    ? 'bg-amber-50 border-amber-200'
    : isOverBudget
    ? 'bg-amber-50 border-amber-200'
    : 'bg-[#F9F7F4] border-[#EDE9E3]';

  const iconClass = balanceError
    ? 'text-amber-500'
    : isOverBudget
    ? 'text-amber-500'
    : 'text-emerald-600';

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 border transition-colors ${containerClass}`}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} strokeWidth={2} aria-hidden />

        {balanceLoading ? (
          <span className="inline-block h-4 w-10 animate-pulse rounded bg-gray-200" aria-label="Loading balance" />
        ) : balanceError ? (
          <span className="text-sm text-amber-700">
            Couldn't load balance
            {onRetryBalance && (
              <button
                type="button"
                onClick={onRetryBalance}
                className="ml-2 inline-flex items-center gap-1 font-semibold underline hover:no-underline"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </button>
            )}
          </span>
        ) : (
          <>
            <span className="text-sm font-bold text-gray-900">
              {balance !== null ? balance.toLocaleString() : '—'}
            </span>
            <span className="text-sm text-gray-500">{unitLabel} available</span>
          </>
        )}
      </div>

      {!balanceError && !balanceLoading && children != null && (
        <div className="flex items-center gap-1.5 text-sm">{children}</div>
      )}
    </div>
  );
};

export default CreditBar;
